import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { JSONTree } from "react-json-tree";
import { CheckIcon } from "@heroicons/react/24/solid";
import * as catalogAPI from "../../../services/catalog";
import * as sourceAPI from "../../../services/sources";
import { SOURCE_LIST } from "../../sources/utils";
import { toFriendlyTime } from "../../../services/utils";
import { theme } from "../../../components/JSONThreeThree";
import { useTheme } from "../../../services/ThemeProvider";

const POLL_MS = 15000;
const FIXED_TYPES = ["cayennelpp", "custom"];

const idOf = (ref) => (ref && typeof ref === "object" ? ref._id : ref) || null;
const groupInFours = (serial) => (serial.match(/.{1,4}/g) || []).join(" ");

function Row({ state, title, action, children }) {
  const icon = {
    done: "bg-green-600 text-white",
    waiting: "border-2 border-amber-500",
    info: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400",
    action: "border-2 border-blue-600",
  }[state];
  const label = { done: "Done", waiting: "Waiting", info: "Check", action: "To do" }[state];
  return (
    <li className="flex gap-4 border-t border-gray-100 dark:border-gray-800 px-6 py-5">
      <span className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${icon}`}>
        {state === "done" && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
        {state === "info" && <span className="text-sm">i</span>}
      </span>
      <div className="flex-1">
        <div className="flex justify-between gap-4">
          <p className="font-semibold text-gray-900 dark:text-gray-50">{title}</p>
          <span className="text-sm text-gray-500 dark:text-gray-400">{action || label}</span>
        </div>
        <div className="mt-1 text-gray-600 dark:text-gray-400">{children}</div>
      </div>
    </li>
  );
}

export default function LoraChecklist({ device }) {
  const { resolvedTheme } = useTheme();
  const profile = device.profile || {};
  const sourceId = idOf(device.source) || idOf(profile.source);
  const catalogId = profile.catalogModel
    || (profile.decoder_type && !FIXED_TYPES.includes(profile.decoder_type) ? profile.decoder_type : null);

  const [entry, setEntry] = useState(null);
  const [source, setSource] = useState(null);
  const [showPacket, setShowPacket] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!catalogId) return undefined;
    let alive = true;
    catalogAPI.get(catalogId).then((e) => { if (alive) setEntry(e); }).catch(() => {});
    return () => { alive = false; };
  }, [catalogId]);

  useEffect(() => {
    if (!sourceId) return undefined;
    let alive = true;
    // A failed check keeps the last result; the next tick tries again.
    const check = () => sourceAPI.findOne(sourceId).then((s) => { if (alive) setSource(s); }).catch(() => {});
    check();
    const timer = setInterval(check, POLL_MS);
    return () => { alive = false; clearInterval(timer); };
  }, [sourceId]);

  const modelLabel = entry ? `${entry.make} ${entry.model}` : profile.name;
  const powerOnLabel = entry ? modelLabel : device.name;
  const channels = entry ? entry.channels : (profile.capabilities || []);
  const logs = (source && source.logs) || [];
  const newest = logs.length ? logs[logs.length - 1] : null; // the API appends
  const network = (source && (SOURCE_LIST.find((p) => p.value === source.provider) || {}).name) || "your network server";

  let packet = null;
  if (showPacket && newest) {
    try { packet = JSON.parse(newest.message); } catch (err) { packet = { message: newest.message }; }
  }

  let decoder;
  if (catalogId) {
    decoder = { state: "done", text: `The ${modelLabel} decoder is applied, so readings arrive named and with units.` };
  } else if (profile.decoder_type === "custom" && !profile.decoder) {
    decoder = { state: "action", text: null };
  } else if (profile.decoder_type === "custom") {
    decoder = { state: "done", text: "Your custom decoder is applied." };
  } else {
    decoder = { state: "done", text: "CayenneLPP: channels come from your payload." };
  }

  const copyResetRef = useRef(null);

  useEffect(() => () => {
    if (copyResetRef.current) clearTimeout(copyResetRef.current);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(device.serial);
      setCopied(true);
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
      copyResetRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopied(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-3xl font-semibold text-gray-900 dark:text-gray-50">{device.name} added</h2>
        <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
          <span className="text-amber-500">●</span> Waiting for first uplink
        </span>
      </div>
      <p className="mt-1 text-gray-600 dark:text-gray-400">{modelLabel}{source ? ` · via ${source.name}` : ""}</p>

      {channels.length > 0 && (
        <div className="mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
          <p className="font-semibold text-gray-900 dark:text-gray-50">Waiting for its first uplink on</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {channels.map((c) => (
              <div key={c.channel} className="w-44 rounded-lg border border-dashed border-gray-300 dark:border-gray-800 p-4">
                <span className="block text-sm text-gray-600 dark:text-gray-400">{c.name}</span>
                <span className="block font-mono text-xl text-gray-400 dark:text-gray-500">waiting…</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="flex justify-between px-6 py-5">
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-50">Setup checklist</p>
          <span className="text-sm text-gray-500 dark:text-gray-400">Checks run automatically</span>
        </div>
        <ul>
          {!sourceId ? (
            <Row state="action" title="No source linked">
              Link this device to a source on the <Link to="/sources" className="text-blue-700 dark:text-blue-400">Sources</Link> page.
            </Row>
          ) : (
            <Row state={newest ? "done" : "waiting"} title={`Source "${source ? source.name : "…"}" is receiving packets`}>
              {newest ? (
                <>
                  <p>Last packet {toFriendlyTime(newest.timestamp)}.</p>
                  <button type="button" className="mt-1 text-blue-700 dark:text-blue-400" onClick={() => setShowPacket((v) => !v)}>
                    {showPacket ? "Hide last packet" : "View last packet"}
                  </button>
                  {packet && (
                    <div className="mt-2 rounded border border-gray-200 dark:border-gray-800 p-4">
                      <JSONTree theme={{ extend: theme }} data={packet} invertTheme={resolvedTheme === "dark"} hideRoot />
                    </div>
                  )}
                </>
              ) : (
                <p>Waiting for a packet from your network server.</p>
              )}
            </Row>
          )}

          <Row state={decoder.state} title="Decoder ready">
            {decoder.text || (
              <Link to={`/profiles/${profile._id}/settings`} className="text-blue-700 dark:text-blue-400">Add a decoder</Link>
            )}
          </Row>

          <Row state="info" title={`Registered in ${network}`}>
            <p>
              Make sure the device is in the application behind {source ? `"${source.name}"` : "your source"} with this DevEUI.
            </p>
            <div className="mt-3 flex gap-2">
              <code className="flex-1 rounded-lg bg-gray-100 dark:bg-gray-800 px-4 py-3 font-mono">{groupInFours(device.serial || "")}</code>
              <button type="button" onClick={copy} className="rounded-lg border border-gray-200 dark:border-gray-800 px-4">
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </Row>

          <Row state="waiting" title={`First uplink from ${device.name}`}>
            Power on the {powerOnLabel}. It joins on its own and usually reports within a few minutes.
            This page updates by itself.
          </Row>
        </ul>
      </div>
    </div>
  );
}
